import axios from "axios";
import { getApiBaseUrl, getHeaders } from "@/api/apiClient";

// Direct StoreClaim client (MERCH-03). Mirrors fbGroupReviewService: axios + getApiBaseUrl()
// + getHeaders(), BASE with no `/api` prefix (getApiBaseUrl already carries it), and every
// call unwraps to the repo `{ ok, data } | { ok, error }` shape.
//
// Replaces the retired OwnershipClaim lead-form client (D-01). The backend POST /api/claims
// reads the evidence fields (phone, pageUrl, note, proofImage) at the TOP LEVEL of the body
// (merchantClaimController.buildEvidence), NOT nested under an `evidence` key — submitClaim
// accepts a structured payload for ergonomics and flattens it onto the wire body to match.

const BASE = "/claims";

export type ClaimStatus = "pending" | "approved" | "rejected" | "revoked";

// The public fields GET /claims/search maps each hit to. The claim search spans published AND
// draft stores, so `isDraft` marks stores that exist but are not yet published — the UI badges
// them with "غير منشور بعد". They are still fully claimable.
export type ClaimStoreResult = {
  id: string;
  name: string;
  slug: string;
  logoUrl?: string;
  reviewCount: number;
  isDraft?: boolean;
};

export type ClaimEvidence = {
  phone?: string;
  pageUrl?: string;
  note?: string;
  proofImageUrl?: string;
};

export type MyClaim = {
  id: string;
  storeId: string;
  store?: {
    name: string;
    slug?: string;
    logoUrl?: string;
    rating?: number;
    totalReviews?: number;
  };
  status: ClaimStatus;
  evidence?: ClaimEvidence;
  rejectionReason?: string;
  createdAt?: string;
};

// Base64 proof image, same shape reviews use (reviewService.uploadReviewImages).
export type ProofImage = {
  data: string; // raw base64, no data-URL prefix
  mimeType: string;
  fileName: string;
};

export type ClaimEvidenceInput = {
  phone: string;
  pageUrl: string;
  note?: string;
  proofImage?: ProofImage;
};

// Either an existing store (storeId) OR the store-not-found auto-scrape path (targetUrl).
export type SubmitClaimPayload = {
  storeId?: string;
  targetUrl?: string;
  name?: string;
  evidence: ClaimEvidenceInput;
};

type ClaimResult<T> = { ok: true; data: T } | { ok: false; error: string };

const unwrapError = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error) && error.response?.data?.error) {
    return error.response.data.error as string;
  }
  return fallback;
};

// UI-SPEC error copy fallbacks.
const SEARCH_FAIL = "تعذّر البحث الآن. تحقق من اتصالك وحاول مجدداً.";
const SUBMIT_FAIL =
  "تعذّر إرسال طلبك. حاول مرة أخرى، وإذا استمرت المشكلة تواصل معنا.";
const STATUS_FAIL = "تعذّر تحميل حالة طلبك. حاول مرة أخرى.";

export const searchStores = async (
  q: string
): Promise<ClaimResult<ClaimStoreResult[]>> => {
  try {
    const response = await axios.get(`${getApiBaseUrl()}${BASE}/search`, {
      params: { q },
      headers: getHeaders(),
    });
    return response.data;
  } catch (error) {
    return { ok: false, error: unwrapError(error, SEARCH_FAIL) };
  }
};

export const submitClaim = async (
  payload: SubmitClaimPayload
): Promise<ClaimResult<{ claimId: string }>> => {
  const { storeId, targetUrl, name, evidence } = payload;
  // Flatten evidence onto the top-level body — the backend reads phone/pageUrl/note/
  // proofImage directly off req.body (not a nested `evidence` object).
  const body: Record<string, unknown> = {
    phone: evidence.phone,
    pageUrl: evidence.pageUrl,
  };
  if (storeId) body.storeId = storeId;
  if (targetUrl) body.targetUrl = targetUrl;
  if (name) body.name = name;
  if (evidence.note) body.note = evidence.note;
  if (evidence.proofImage) body.proofImage = evidence.proofImage;

  try {
    const response = await axios.post(`${getApiBaseUrl()}${BASE}`, body, {
      headers: getHeaders(),
    });
    return response.data;
  } catch (error) {
    return { ok: false, error: unwrapError(error, SUBMIT_FAIL) };
  }
};

export const getMyClaims = async (): Promise<ClaimResult<MyClaim[]>> => {
  try {
    const response = await axios.get(`${getApiBaseUrl()}${BASE}/mine`, {
      headers: getHeaders(),
    });
    return response.data;
  } catch (error) {
    return { ok: false, error: unwrapError(error, STATUS_FAIL) };
  }
};

// ── Claim OTP (instant approval via the store's registered number) ──────────
// The client only ever sees masked numbers and opaque ids — it never sends a phone number.

export type ClaimOtpOption = { id: string; masked: string };

const OTP_FAIL = "تعذّر إتمام التحقق الآن. حاول مرة أخرى.";

export const getClaimOtpOptions = async (
  storeId: string
): Promise<ClaimResult<{ enabled: boolean; options: ClaimOtpOption[] }>> => {
  try {
    const response = await axios.get(`${getApiBaseUrl()}${BASE}/otp/options`, {
      params: { storeId },
      headers: getHeaders(),
    });
    return response.data;
  } catch (error) {
    return { ok: false, error: unwrapError(error, OTP_FAIL) };
  }
};

export const initiateClaimOtp = async ({
  storeId,
  phoneId,
}: {
  storeId: string;
  phoneId: string;
}): Promise<ClaimResult<{ transactionId: string; masked: string }>> => {
  try {
    const response = await axios.post(
      `${getApiBaseUrl()}${BASE}/otp/initiate`,
      { storeId, phoneId },
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    return { ok: false, error: unwrapError(error, OTP_FAIL) };
  }
};

export const verifyClaimOtp = async ({
  storeId,
  transactionId,
  otp,
  whatsappOptIn,
}: {
  storeId: string;
  transactionId: string;
  otp: string;
  // D-16: explicit WhatsApp consent for the store's number. Sent only when true.
  whatsappOptIn?: boolean;
}): Promise<ClaimResult<{ claimId: string; status: "approved" }>> => {
  try {
    const response = await axios.post(
      `${getApiBaseUrl()}${BASE}/otp/verify`,
      {
        storeId,
        transactionId,
        otp,
        ...(whatsappOptIn === true ? { whatsappOptIn: true } : {}),
      },
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    return { ok: false, error: unwrapError(error, OTP_FAIL) };
  }
};
